/*
 * Tab behaviour for converted Replo sections.
 *
 * Replo renders tabs with Radix, and the conversion bakes the rendered HTML in
 * with the runtime stripped, so the triggers became dead markup. Confirmed
 * regression: AIR PRO "Competitors" changes content on live and did nothing
 * here. This reproduces the contract the DOM already declares:
 *
 *   [role="tablist"]
 *     button[role="tab"][data-replo-tabs-trigger="true"]
 *         aria-controls -> the panel id, aria-selected, data-state
 *   div[role="tabpanel"][id]
 *         active   -> style="display:contents", data-state="active"
 *         inactive -> style="display:none",     data-state="inactive"
 *
 * Panels are all present in the markup - Radix would unmount the inactive one,
 * but the extraction captured both, so no markup changes are needed.
 *
 * The visual state of a trigger is NOT a modifier class. Replo emits a distinct
 * atomic class per computed style, so the selected trigger carries a different
 * class set to an unselected one (AIR PRO s3: the selected pill adds a class
 * whose only payload is background-color:#000). Nothing in the DOM says which
 * set means "selected", so we learn it: the subtrees of the triggers in a group
 * are structurally identical, so we snapshot each one's classes positionally at
 * load, take the initially-selected trigger's snapshot as the selected look and
 * an unselected one as the other, then swap them on click.
 *
 * Grouping is by the Radix instance id embedded in the panel id
 * ("radix-:R7rr2:-content-<uuid>"), because a section can hold more than one
 * tab group (AIR PRO s2 has two) and the panels are not descendants of the
 * tablist.
 */
(function () {
  'use strict';

  var TRIGGER = '[data-replo-tabs-trigger="true"]';

  // "radix-:R7rr2:-content-af47b091..." -> "radix-:R7rr2:"
  function groupKey(trigger) {
    var id = trigger.getAttribute('aria-controls') || '';
    var m = id.match(/^(radix-:[^:]*:)/);
    if (m) return m[1];
    var list = trigger.closest('[role="tablist"]');
    if (list) {
      if (!list.dataset.tabGroupKey) list.dataset.tabGroupKey = 'tablist-' + (groupKey.n = (groupKey.n || 0) + 1);
      return list.dataset.tabGroupKey;
    }
    return null;
  }

  function panelFor(trigger) {
    var id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function isSelected(trigger) {
    return trigger.getAttribute('aria-selected') === 'true';
  }

  // Class lists of every descendant, in document order. The button itself
  // carries no class (Replo gives it display:contents), so it is skipped.
  function snapshot(trigger) {
    return [].map.call(trigger.querySelectorAll('*'), function (el) {
      return el.getAttribute('class') || '';
    });
  }

  function applyLook(trigger, look) {
    if (!look) return;
    var nodes = trigger.querySelectorAll('*');
    // Only safe when the subtrees line up; otherwise leave the styling alone
    // rather than scramble it, and let the state attributes carry the change.
    if (nodes.length !== look.length) return;
    for (var i = 0; i < nodes.length; i++) {
      if (look[i]) nodes[i].setAttribute('class', look[i]);
      else nodes[i].removeAttribute('class');
    }
  }

  function select(group, trigger) {
    group.triggers.forEach(function (t) {
      var on = t === trigger;

      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('data-state', on ? 'active' : 'inactive');
      // roving tabindex, as the tablist role implies
      t.setAttribute('tabindex', on ? '0' : '-1');
      applyLook(t, on ? group.selectedLook : group.unselectedLook);

      var panel = panelFor(t);
      if (!panel) return;
      panel.setAttribute('data-state', on ? 'active' : 'inactive');
      // mirror Replo's own inline styles; animation-duration:0s is what it
      // emits on the mounted panel and keeps the swap from animating in
      panel.style.display = on ? 'contents' : 'none';
      if (on) panel.style.animationDuration = '0s';
    });
  }

  function bindGroup(group) {
    group.triggers.forEach(function (t) {
      if (t.dataset.tabsBound === 'true') return;
      t.dataset.tabsBound = 'true';

      t.addEventListener('click', function (e) {
        e.preventDefault();
        select(group, t);
      });

      t.addEventListener('keydown', function (e) {
        var vertical = (t.getAttribute('data-orientation') || 'horizontal') === 'vertical';
        var fwd = vertical ? 'ArrowDown' : 'ArrowRight';
        var back = vertical ? 'ArrowUp' : 'ArrowLeft';
        var i = group.triggers.indexOf(t);
        var next = null;

        if (e.key === fwd) next = group.triggers[(i + 1) % group.triggers.length];
        else if (e.key === back) next = group.triggers[(i - 1 + group.triggers.length) % group.triggers.length];
        else if (e.key === 'Home') next = group.triggers[0];
        else if (e.key === 'End') next = group.triggers[group.triggers.length - 1];
        else return;

        e.preventDefault();
        select(group, next);
        next.focus();
      });
    });
  }

  function bind() {
    var groups = {};

    document.querySelectorAll(TRIGGER).forEach(function (t) {
      var key = groupKey(t);
      if (!key) return;
      if (!groups[key]) groups[key] = { triggers: [], selectedLook: null, unselectedLook: null };
      groups[key].triggers.push(t);
    });

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      if (group.triggers.length < 2) return;

      // Learn the two looks from the state the markup shipped in.
      group.triggers.forEach(function (t) {
        if (isSelected(t)) {
          if (!group.selectedLook) group.selectedLook = snapshot(t);
        } else if (!group.unselectedLook) {
          group.unselectedLook = snapshot(t);
        }
      });

      group.triggers.forEach(function (t) {
        t.setAttribute('tabindex', isSelected(t) ? '0' : '-1');
      });

      bindGroup(group);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  // the theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', bind);
})();
